import { IcBack, IcGear, IcPush } from './Icons';
import { StatusBadge, Hero, Tiles, DayLog, Week, Tools, PerfLink, Details, HelpPanel } from './parts';
import '../../css/screensHoney.css';
import '../../css/screenMerged.css';

/**
 * The merged one-screen page (.claude/pp-screens-merged-prototype.html): status first, then what is playing, then a plain day log;
 * tools and details on the side. PURE: every existing component/handler (live preview, screenshots + capture, assign campaign,
 * rotation, settings modal, restart / power / flush, performance) is passed in by Show.jsx — nothing is rebuilt here.
 *
 * Props: c (customer block) | loading | failedLoad; preview/rotation/geo are the EXISTING nodes; handlers come from Show.jsx.
 */
export default function MergedScreen({
  t, lang, c, loading, loadFailed, onRetry,
  name, serial, onBack,
  ops, setOp, failed, supportVars,
  activeTab, setActiveTab, preview, campaignName, onAssign, onSettings,
  rotation, hoursText, detailRows, geo, canPower, perfEnabled, onPerf, muted, canMute, pausedNode,
  logDay, onLogDay, dayLog,
}) {
  const tz = c?.device?.timezone;
  const weekDates = (c?.week || []).map((d) => d.date);
  const todayDate = weekDates[weekDates.length - 1];
  // a selection that fell out of the 7-day window (midnight passed) is today again
  const day = logDay && weekDates.includes(logDay) && logDay !== todayDate ? logDay : null;
  return (
    <div className="pp-sp" data-testid="merged-screen-page">
      <button type="button" className="pp-sp-crumb" onClick={onBack}><IcBack />{t('sh.screens')}</button>
      <div className="pp-sp-shead">
        <div className="pp-sp-nm">
          <h1>{name || ''}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {c ? <StatusBadge t={t} state={c.state} detail={c.playback_detail} /> : null}
            <span className="pp-sp-hint">{[c?.company_name, c?.device?.branch].filter(Boolean).join(' — ')}</span>
          </div>
          {pausedNode ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>{pausedNode}</div> : null}
          <span className="pp-sp-id pp-sp-ltr">ID: {serial}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="pp-sp-btn pp-sp-primary" onClick={onAssign}><IcPush />{t('sh.push_content')}</button>
          <button type="button" className="pp-sp-btn pp-sp-ghost" aria-label={t('sh.settings_aria')} onClick={onSettings}><IcGear />{t('sh.settings')}</button>
        </div>
      </div>

      <div className="pp-sp-cols">
        <div className="pp-sp-main">
          {loading ? (
            <div aria-busy="true" data-testid="sh-screen-loading" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="pp-sp-skel" style={{ height: 190 }} />
              <div className="pp-sp-tiles"><div className="pp-sp-skel" style={{ height: 72 }} /><div className="pp-sp-skel" style={{ height: 72 }} /><div className="pp-sp-skel" style={{ height: 72 }} /></div>
            </div>
          ) : !c ? (
            <section className="pp-sp-panel" data-testid="sh-screen-error">
              <h3>{t('sh.status_error')}</h3>
              <p className="pp-sp-hint" style={{ margin: 0 }}>{t('sh.error_body')}</p>
              {loadFailed ? <button type="button" className="pp-sp-btn pp-sp-ghost" style={{ alignSelf: 'flex-start' }} onClick={onRetry}>{t('sh.retry')}</button> : null}
            </section>
          ) : (
            <>
              <Hero t={t} lang={lang} c={c} ops={ops} setOp={setOp} failed={failed} support={c.support} vars={supportVars} onHours={onSettings} opName={c.device?.name} />
              <Tiles t={t} comps={c.components} muted={muted} />
            </>
          )}

          <section className="pp-sp-panel" data-testid="sh-nowplaying">
            <div className="pp-sp-ph">
              <h3>{t('sh.now_playing')}</h3>
              <div className="pp-scr-seg" role="group">
                <button type="button" className={activeTab === 'preview' ? 'on' : ''} onClick={() => setActiveTab('preview')}>{t('sh.tab_live')}</button>
                <button type="button" className={activeTab === 'screenshot' ? 'on' : ''} onClick={() => setActiveTab('screenshot')}>{t('sh.tab_shot')}</button>
              </div>
            </div>
            {preview}
            <div className="pp-sp-camp">
              <div className="pp-sp-cl"><span className="pp-sp-hint">{t('sh.assigned_campaign')}</span><b>{campaignName || t('sh.no_campaign')}</b></div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={onAssign}>{t('sh.change_campaign')}</button>
              </div>
            </div>
          </section>

          {c ? <DayLog t={t} lang={lang} log={day ? dayLog?.data?.log : c.log} tz={tz} day={day} todayDate={todayDate} onToday={() => onLogDay && onLogDay(null)}
            loading={!!day && !!dayLog?.isLoading} failed={!!day && !!dayLog?.isError} /> : null}
          {c ? <Week t={t} lang={lang} week={c.week} selected={day} onSelect={onLogDay} /> : null}
        </div>

        <div className="pp-sp-side">
          {c ? <Tools t={t} c={c} ops={ops} setOp={setOp} failed={failed} opName={c.device?.name} rotation={rotation}
            hours={hoursText} onEditHours={onSettings} canPower={canPower} muted={muted} canMute={canMute} /> : null}
          <PerfLink t={t} enabled={perfEnabled} onClick={onPerf} />
          <Details t={t} rows={detailRows} />
          {geo}
          {c ? <HelpPanel t={t} lang={lang} support={c.support} vars={supportVars} ops={ops} setOp={setOp} /> : null}
        </div>
      </div>
    </div>
  );
}
